import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Checkbox, FormControlLabel, TextField } from '@material-ui/core';
import styled from 'styled-components/macro';
import { isEmpty, has } from 'lodash';
import BusinessIcon from '@mui/icons-material/Business';
import RoomIcon from '@mui/icons-material/Room';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';

import Form from 'components/ui/Form';
import { border } from 'variables/general';
import { brand, brandDark, error } from 'variables/colors';
import { FlexCenterVertical } from 'styles/flexbox';
import Dialog from 'components/ui/Dialog';
import ScheduleAppointmentModal from 'components/general/SchduleAppointmentModal';
import GeneralInfoFormFields from 'components/general/GeneralInfoFormFields';
import { fontWeightSemiBold } from 'variables/font';
import { siteNameGenerator } from 'helpers/site-name-generator';
import TermsDetails from './TermsDetails';

const Details = styled.div`
  display: grid;
  grid-gap: 1em;
  grid-template-columns: 1fr;
  padding: 1.5em 1.5em;
  border-bottom: ${border};

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1fr;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-row-gap: 1em;
  grid-column-gap: 1em;
  grid-template-columns: 1fr;

  @media (min-width: 30em) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Detail = styled(FlexCenterVertical)`
  word-break: break-word;
`;

const Label = styled.h5`
  margin-left: 0.875em;
`;

const Component = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 30em) {
    flex: 1 1 auto;
    height: 10em;
  }
`;

const FormContainer = styled.div`
  margin-bottom: 5em;
  padding: 1.5em;
  overflow-y: auto;

  @media (min-width: 30em) {
    margin-bottom: initial;
  }
`;

const TermsHeading = styled.h3`
  margin-bottom: 2em;
  line-height: 1.5;
`;

const TermsLink = styled.a`
  color: ${brand};
  text-decoration: underline;

  &:hover {
    color: ${brandDark};
  }
`;

const CheckAgreement = styled.div`
  grid-column: 1 / -1;
`;

const Check = styled(FlexCenterVertical)`
  font-size: 1em;

  .MuiFormControlLabel-root {
    margin-right: 0.3em;
  }
`;

const ErrorMessage = styled.p`
  color: ${error};
  font-weight: ${fontWeightSemiBold};
  font-size: 0.75em;
`;

const Confirm = ({
  organization,
  selectedSlot,
  selectedDate,
  setStep,
  firstName,
  lastName,
  email,
  phone,
  dateOfBirth,
  touched,
  errors,
  handleChange,
  showBirthDate,
  submitForm,
  agree,
  setFieldValue,
  subLocation,
  selectService,
  schedule,
  customFieldValue,
}) => {
  const [checked, setChecked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  if (isEmpty(organization)) {
    return null;
  }

  const handleCheck = () => {
    setChecked(!checked);
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleAcceptTerms = () => {
    handleCheck();
    handleModalClose();
  };

  const { name, logo } = organization;
  const { start_date: startDate, end_date: endDate } = selectedSlot;

  const startTime = moment(startDate, 'YYYY-MM-DD hh:mm:ss').format('h:mm');
  const endTime = moment(endDate, 'YYYY-MM-DD hh:mm:ss').format('h:mma');

  const details = [
    {
      label: name,
      icon: <BusinessIcon />,
    },
    {
      label: siteNameGenerator(organization, subLocation),
      icon: <RoomIcon />,
    },
    {
      label: selectService.name,
      icon: <HealthAndSafetyOutlinedIcon />,
    },
    {
      label: `${moment(selectedDate, 'YYYY-MM-DD hh:mm:ss').format(
        'dddd, MMMM DD, YYYY',
      )}`,
      icon: <CalendarTodayIcon />,
    },
    {
      label: `${startTime} - ${endTime}`,
      icon: <AccessTimeIcon />,
    },
  ];

  const handlePrev = () => {
    setStep('scheduler');
  };

  const { custom_field: customField } = schedule || {};

  return (
    <ScheduleAppointmentModal
      logo={logo}
      handlePrev={handlePrev}
      handleNext={submitForm}
      maxHeight="50em"
      fullHeight
    >
      <Component>
        <Dialog
          open={modalOpen}
          handleClose={handleModalClose}
          submitForm={handleAcceptTerms}
          buttonText="Accept"
          bookingScheduler
        >
          <TermsHeading>
            COVID-19 Screening Program Consent and Authorization
          </TermsHeading>
          <TermsDetails />
        </Dialog>

        <Details>
          {details.map(({ label, icon }) => (
            <Detail>
              {icon}
              <Label>{label}</Label>
            </Detail>
          ))}
        </Details>

        <FormContainer>
          <Form fullWidth onSubmit={submitForm}>
            <GridContainer>
              <GeneralInfoFormFields
                firstName={firstName}
                lastName={lastName}
                email={email}
                dateOfBirth={dateOfBirth}
                phone={phone}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                showBirthDate={showBirthDate}
                phoneRequired={false}
              />

              {customField ? (
                <TextField
                  color="primary"
                  error={
                    touched.customFieldValue && has(errors, 'customFieldValue')
                  }
                  fullWidth
                  helperText={
                    touched.customFieldValue && errors.customFieldValue
                  }
                  label={customField}
                  name="customFieldValue"
                  onChange={handleChange}
                  value={customFieldValue}
                  type="customFieldValue"
                  variant="outlined"
                  autoComplete="chrome-off"
                  required
                />
              ) : (
                <div />
              )}

              <CheckAgreement>
                <Check>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={agree}
                        onChange={handleChange}
                        name="agree"
                        size="small"
                      />
                    }
                    label={<p>I agree to the</p>}
                  />
                  <TermsLink onClick={handleModalOpen}>
                    terms & conditions
                  </TermsLink>
                </Check>
                {touched.agree && has(errors, 'agree') && (
                  <ErrorMessage>{errors.agree}</ErrorMessage>
                )}
              </CheckAgreement>
            </GridContainer>
          </Form>
        </FormContainer>
      </Component>
    </ScheduleAppointmentModal>
  );
};

export default Confirm;
